import { useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { FileBarChart, MoreHorizontal, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { QueryError } from "@/components/QueryError";
import { OwnerPicker } from "@/components/OwnerPicker";
import { useMvas, useMvaMutations, type MvaRow, type MvaScope } from "@/hooks/useMvas";
import { MVA_STATUSES, MVA_DELIVERED, isOpenMva, isMvaOverdue, localIsoDate, tallyByAssignee, type MvaStatus } from "@shared/mvaTracker";

const STATUS_STYLES: Record<string, string> = {
  "Not started": "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300",
  "Waiting on financials": "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  "In progress": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  "In review": "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  "Delivered": "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
};
export const mvaStatusClass = (s: string | null | undefined) => STATUS_STYLES[s ?? ""] ?? STATUS_STYLES["Not started"];

const companyHref = (r: MvaRow) => (r.category === "Client" ? `/clients/${r.companyId}` : `/prospects/${r.companyId}`);
const shortDate = (iso: string | null) =>
  iso ? new Date(iso.length === 10 ? iso + "T00:00:00" : iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
const daysBetween = (fromIso: string, toIso?: string | null) =>
  Math.max(0, Math.floor(((toIso ? Date.parse(toIso) : Date.now()) - Date.parse(fromIso)) / 86_400_000));

/** Click-to-edit note. Enter or blur saves, Escape cancels. */
function NotesCell({ value, onSave }: { value: string | null; onSave: (v: string | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const cancelled = useRef(false);
  if (!editing) {
    return (
      <button
        className="text-left w-full rounded px-1 py-0.5 min-h-[26px] hover:bg-muted/50 whitespace-pre-wrap break-words"
        onClick={() => { setDraft(value ?? ""); cancelled.current = false; setEditing(true); }}
      >
        {value || <span className="text-muted-foreground italic">Add note</span>}
      </button>
    );
  }
  const commit = () => {
    setEditing(false);
    if (cancelled.current) return;
    const next = draft.trim() || null;
    if (next !== (value ?? null)) onSave(next);
  };
  return (
    <Input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") { cancelled.current = true; setEditing(false); }
      }}
      className="h-7 text-xs"
      placeholder="e.g. waiting on 2025 P&L"
    />
  );
}

/**
 * "MVAs in progress" on the Prospects page: who is working on which MVA,
 * where each one stands, and a count per person. Everything is editable in
 * place so nobody has to open the prospect to update it.
 */
export function MvaTracker({ onStart, onClose }: { onStart: () => void; onClose: () => void }) {
  const [scope, setScope] = useState<MvaScope>("open");
  // null = everyone, "" = unassigned, otherwise a user id.
  const [who, setWho] = useState<string | null>(null);
  const { data: rows = [], isLoading, error, refetch, isFetching } = useMvas(scope);
  const { data: openRows = [] } = useMvas("open");
  const { update, remove } = useMvaMutations();
  const [confirmRemove, setConfirmRemove] = useState<MvaRow | null>(null);

  const today = localIsoDate();
  const tally = useMemo(() => tallyByAssignee(openRows, today), [openRows, today]);
  const shown = who === null ? rows : rows.filter((r) => (r.assignedTo ?? "") === who);

  const deliver = (r: MvaRow) =>
    update.mutate(
      { id: r.id, status: MVA_DELIVERED },
      { onSuccess: () => toast.success(`${r.companyName}: MVA delivered`, { description: r.category === "Client" ? undefined : "The prospect moves to MVA delivered." }) },
    );

  const chip = (key: string | null, label: string, count: number, overdue = 0) => {
    const on = who === key;
    return (
      <button
        key={key ?? "__all__"}
        onClick={() => setWho(on ? null : key)}
        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted/50"}`}
      >
        <span className="font-medium">{label}</span>
        <span className={on ? "opacity-80" : "text-muted-foreground"}>{count}</span>
        {overdue > 0 && <span className={on ? "" : "text-red-600 dark:text-red-400"}>· {overdue} late</span>}
      </button>
    );
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          <FileBarChart className="w-4 h-4 shrink-0 text-muted-foreground" />
          <span className="font-semibold text-sm">MVAs in progress</span>
          <span className="text-xs text-muted-foreground truncate">Who is working on which MVA, and where each one stands</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex rounded-md border p-0.5">
            {(["open", "delivered"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={`px-2 py-0.5 text-xs rounded ${scope === s ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
              >
                {s === "open" ? "In progress" : "Delivered"}
              </button>
            ))}
          </div>
          <Button size="sm" className="h-7 text-xs gap-1" onClick={onStart}><Plus className="w-3 h-3" />Start MVA</Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close MVA tracker"><X className="w-4 h-4" /></Button>
        </div>
      </div>

      {tally.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-b">
          {chip(null, "Everyone", openRows.length, openRows.filter((r) => isMvaOverdue(r, today)).length)}
          {tally.map((t) => chip(t.userId ?? "", t.name, t.open, t.overdue))}
        </div>
      )}

      {error && <div className="p-3"><QueryError error={error} onRetry={() => void refetch()} busy={isFetching} hasData={rows.length > 0} noun="MVAs" /></div>}

      <table className="w-full text-sm">
        <thead className="bg-muted/20 text-xs">
          <tr>
            <th className="text-left p-2 font-medium">Company</th>
            <th className="text-left p-2 font-medium">Assigned to</th>
            <th className="text-left p-2 font-medium">Status</th>
            <th className="text-left p-2 font-medium">Assigned</th>
            <th className="text-left p-2 font-medium">{scope === "open" ? "Due" : "Delivered"}</th>
            <th className="text-left p-2 font-medium w-[28%]">Notes</th>
            <th className="w-10 p-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {isLoading ? (
            <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
          ) : shown.length === 0 ? (
            <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
              {scope === "open"
                ? who === null ? "No MVAs in progress. Start one here or from a prospect's ⋯ menu." : "Nothing in progress for this person."
                : "No delivered MVAs yet."}
            </td></tr>
          ) : shown.map((r) => {
            const open = isOpenMva(r.status);
            const late = isMvaOverdue(r, today);
            return (
              <tr key={r.id} className="align-top hover:bg-muted/20">
                <td className="p-2">
                  <Link href={companyHref(r)} className="font-medium hover:underline">{r.companyName}</Link>
                  <div className="text-xs text-muted-foreground">{[r.industry, r.category === "Client" ? "Client" : r.stage].filter(Boolean).join(" · ") || "—"}</div>
                </td>
                <td className="p-2">
                  <OwnerPicker value={r.assignedTo} onChange={(v) => update.mutate({ id: r.id, assignedTo: v })} />
                </td>
                <td className="p-2">
                  <Select value={r.status} onValueChange={(v) => update.mutate({ id: r.id, status: v as MvaStatus })}>
                    <SelectTrigger className={`h-7 text-xs w-[172px] border-transparent ${mvaStatusClass(r.status)}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{MVA_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  {open && r.docUploadedAt && (
                    <button className="block mt-1 text-[11px] text-green-700 dark:text-green-400 hover:underline" onClick={() => deliver(r)}>
                      MVA doc uploaded {shortDate(r.docUploadedAt)}. Mark delivered?
                    </button>
                  )}
                </td>
                <td className="p-2 text-xs whitespace-nowrap">
                  <div>{shortDate(r.assignedAt)} <span className="text-muted-foreground">· {daysBetween(r.assignedAt, r.deliveredAt)}d</span></div>
                  {r.assignedByName && r.assignedBy !== r.assignedTo && <div className="text-muted-foreground">by {r.assignedByName}</div>}
                </td>
                <td className="p-2 text-xs whitespace-nowrap">
                  {open ? (
                    <Input
                      key={r.dueOn ?? ""}
                      type="date"
                      defaultValue={r.dueOn ?? ""}
                      onBlur={(e) => { const v = e.target.value || null; if (v !== r.dueOn) update.mutate({ id: r.id, dueOn: v }); }}
                      className={`h-7 text-xs w-[138px] ${late ? "border-red-400 text-red-700 dark:text-red-400" : ""}`}
                      title={late ? "Past due" : undefined}
                    />
                  ) : (
                    <span>{shortDate(r.deliveredAt)}</span>
                  )}
                </td>
                <td className="p-2 text-xs">
                  <NotesCell value={r.notes} onSave={(n) => update.mutate({ id: r.id, notes: n })} />
                </td>
                <td className="p-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {open
                        ? <DropdownMenuItem onClick={() => deliver(r)}>Mark delivered</DropdownMenuItem>
                        : <DropdownMenuItem onClick={() => update.mutate({ id: r.id, status: "In review" })}>Reopen</DropdownMenuItem>}
                      <DropdownMenuItem asChild><Link href={companyHref(r)}>Open {r.category === "Client" ? "client" : "prospect"}</Link></DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600 dark:text-red-400" onClick={() => setConfirmRemove(r)}>Remove from tracker</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(v) => !v && setConfirmRemove(null)}
        title="Remove this MVA from the tracker?"
        description={confirmRemove ? `${confirmRemove.companyName} will no longer show as ${isOpenMva(confirmRemove.status) ? "in progress" : "delivered"}. The prospect itself is not changed.` : ""}
        confirmLabel="Remove"
        variant="destructive"
        isLoading={remove.isPending}
        onConfirm={() => { if (confirmRemove) remove.mutate(confirmRemove.id, { onSuccess: () => setConfirmRemove(null) }); }}
      />
    </div>
  );
}
